// Story mode's pieces around the scenes (docs/plans/2026-10-17-journey.md): the doorstep's pickups and path hints, the
// phone panel, the journey card, the poster bazaar near the gate (docs/plans/2026-10-18-poster-bazaar.md), and the
// chime. main.ts owns the worlds and the scene switching; this holds the parts.
import type { GameMap, MapJson, Vec } from '../shared/map';
import { buildField, flowDir } from '../shared/map';
import type { JourneyData, OptionId, Choices, Arrival, BazaarData } from '../shared/journey';
import POSTERS from './posters.json';
import { FONT, INK, tag } from './ui';
import { MODES, Mode, ModeId, LOCKED, openModes } from './travel';

/** posters.png's width (tools/make-poster-sprites.py SHELF_W), for scaling a card cut from it */
const ATLAS_W = 1024;

export interface DoorstepPoints {
  home: Vec;
  /** the metro gate with the shortest walk (journey-routes.py picks it on OSM: Gate 2) */
  gate: Vec; gateName: string;
}

/**
 * Before loading the doorstep map: drop its zone strip (the home lane is not tinted: kind doorstep) and find the
 * pickups. `gateOsm` is the OSM node of the walk's gate.
 */
export function prepareDoorstep(j: MapJson, gateOsm: string): { json: MapJson; points: (m: GameMap) => DoorstepPoints } {
  // the home lane's tiles are ordinary road here: no tint, and no zone barricades across it
  const json: MapJson = { ...j, zone: [], rows: j.rows.map((r) => r.replace(/P/g, '=')), buildings: j.buildings?.map((b) => ({ ...b })) };
  return {
    json,
    points(m) {
      const home = { x: m.spawnsP[0].x, y: m.spawnsP[0].y };
      const g = m.pois?.find((p) => p.osm === gateOsm) ?? m.pois?.find((p) => p.g === 'metro');
      if (!g) throw new Error('the doorstep map has no metro gate');
      return { home, gate: { x: g.x, y: g.y }, gateName: g.name || 'Metro gate' };
    },
  };
}

/** The walking path from `from` to `to` on the map's tiles (a flow field towards `to`), as points a tile apart. */
export function pathLine(m: GameMap, to: Vec, from: Vec, max = 300): Vec[] {
  const F = buildField(m, [to]), out: Vec[] = [{ x: from.x, y: from.y }];
  let x = from.x, y = from.y;
  for (let i = 0; i < max; i++) {
    const tx = Math.floor(x / m.tile), ty = Math.floor(y / m.tile);
    if (F[ty * m.w + tx] <= 0) break;
    const d = flowDir(m, F, x, y);
    if (!d) break;
    x = (Math.floor(x / m.tile) + 0.5 + Math.round(d.x)) * m.tile; y = (Math.floor(y / m.tile) + 0.5 + Math.round(d.y)) * m.tile;
    out.push({ x, y });
  }
  out.push({ x: to.x, y: to.y });
  return out;
}

/** The path's length in world px. */
export const pathLength = (p: Vec[]) => p.reduce((a, q, i) => (i ? a + Math.hypot(q.x - p[i - 1].x, q.y - p[i - 1].y) : 0), 0);

/** A dotted path hint on the ground, in world coordinates (the caller has applied the camera). */
export function drawPath(g: CanvasRenderingContext2D, p: Vec[], z: number, t: number) {
  if (p.length < 2) return;
  g.save();
  g.strokeStyle = 'rgba(248,196,10,.9)'; g.lineWidth = 5 / z; g.lineCap = 'round'; g.setLineDash([2 / z, 14 / z]); g.lineDashOffset = -t * 30 / z;
  g.beginPath(); g.moveTo(p[0].x, p[0].y); for (const q of p) g.lineTo(q.x, q.y); g.stroke();
  g.restore();
}

/**
 * The bazaar's stall: `inM` metres inside the gate along the walk in towards `towards` (the plaza), so its marker and
 * the gate's don't overlap. World px.
 */
export function stallPoint(m: GameMap, gate: Vec, towards: Vec, inM: number): Vec {
  const p = pathLine(m, towards, gate), want = inM * (m.mpt ? m.tile / m.mpt : 15);
  let d = 0;
  for (let i = 1; i < p.length; i++) {
    const s = Math.hypot(p[i].x - p[i - 1].x, p[i].y - p[i - 1].y);
    if (d + s >= want) { const k = (want - d) / s; return { x: p[i - 1].x + (p[i].x - p[i - 1].x) * k, y: p[i - 1].y + (p[i].y - p[i - 1].y) * k }; }
    d += s;
  }
  return p[p.length - 1];
}

/** A labelled marker standing over a world point (the gate, the pin, the stall). World coordinates. */
export function drawMarker(g: CanvasRenderingContext2D, at: Vec, label: string, z: number, colour: string = INK.metro) {
  g.save(); g.translate(at.x, at.y); g.scale(1 / z, 1 / z);
  g.fillStyle = colour; g.strokeStyle = INK.asphalt; g.lineWidth = 2.5;
  g.beginPath(); g.moveTo(0, 0); g.lineTo(-9, -18); g.arc(0, -22, 10, Math.PI * 0.82, Math.PI * 0.18); g.closePath(); g.fill(); g.stroke();
  g.beginPath(); g.arc(0, -22, 3.5, 0, Math.PI * 2); g.fillStyle = INK.asphalt; g.fill();
  tag(g, label.toUpperCase(), 0, -64, { font: FONT.display(17), fill: INK.newsprint, align: 'center', h: 24, shadow: 3 });
  g.restore();
}

// ------------------------------------------------------------------ the phone

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

/** The phone panel: the group chat, then the ways to go (keys 1, 2), and a replay's "same as last time" (key 3). */
/**
 * The phone: story mode's chat and how you get there, and online, after the doomscroll, the same question
 * (docs/plans/2026-10-18-shared-metro.md, section 7). Only the open modes have a number key; the rest show locked.
 */
export class Phone {
  constructor(private el: HTMLElement, private modes: Set<ModeId> = openModes(location.search)) {}
  open = false;
  private picks: (() => void)[] = [];
  show(J: JourneyData, place: string, onPick: (o: OptionId) => void, last: Choices | null, onSame: () => void) {
    const chat = J.phone.chat.map((c) => `<div class="msg"><b>${esc(c.from)}</b><span>${esc(c.en)}</span>${c.hi ? `<span class="hi" lang="hi">${esc(c.hi)}</span>` : ''}</div>`).join('');
    // every mode, in the phone's order: the episode's own wording where it has one, open only if it is built and unlocked
    const items = MODES.map((m) => {
      const o = J.options.find((x) => x.id === m.id);
      return { mode: o ? { ...m, en: o.label.en, pitch: o.pitch.en } : m, go: o && this.modes.has(m.id) ? () => onPick(o.id) : null };
    });
    const same = last && this.modes.has(last.option) ? { text: lastText(last), go: onSame } : null;
    this.el.classList.remove('over');
    this.render(`<div class="ph-top"><span>${esc(J.clock.leaveAt)}</span><span>${esc(place)}</span></div>${chat}`, items, same);
  }
  private render(head: string, items: { mode: Mode; go: (() => void) | null }[], same: { text: string; go: () => void } | null) {
    let k = 0;
    const btn = (it: { mode: Mode; go: (() => void) | null }, i: number) => it.go
      ? `<button data-i="${i}" data-mode="${it.mode.id}"><kbd>${++k}</kbd> <b>${esc(it.mode.en)}</b><span>${esc(it.mode.pitch)}</span></button>`
      : `<button data-i="${i}" data-mode="${it.mode.id}" class="locked" aria-disabled="true"><b>🔒 ${esc(it.mode.en)}</b> <i lang="hi">${esc(it.mode.hi)}</i><span>${esc(LOCKED.en)} · <span lang="hi">${esc(LOCKED.hi)}</span></span></button>`;
    const opts = items.map(btn).join('');
    const sameB = same ? `<button data-same><kbd>${++k}</kbd> <b>Same as last time</b><span>${esc(same.text)}</span></button>` : '';
    this.el.innerHTML = `${head}<div class="ph-q">How do you get there?</div>${opts}${sameB}<p class="ph-note" aria-live="polite"></p>`;
    this.picks = items.filter((it) => it.go).map((it) => it.go!);
    if (same) this.picks.push(same.go);
    this.el.querySelectorAll<HTMLButtonElement>('button[data-i]').forEach((b) => {
      const it = items[Number(b.dataset.i)];
      b.onclick = () => (it.go ? it.go() : this.locked(b, it.mode));
    });
    const sb = this.el.querySelector<HTMLButtonElement>('button[data-same]'); if (sb && same) sb.onclick = same.go;
    this.el.hidden = false; this.open = true;
  }
  /** A locked mode says so, and gives a shake (none under reduced motion). */
  private locked(b: HTMLElement, m: Mode) {
    const note = this.el.querySelector('.ph-note');
    if (note) note.textContent = `${m.en} is in development. Take the metro for now.`;
    b.classList.remove('shake'); void b.offsetWidth; b.classList.add('shake');
  }
  key(code: string): boolean {
    if (!this.open) return false;
    const i = ['Digit1', 'Digit2', 'Digit3', 'Digit4'].indexOf(code.replace('Numpad', 'Digit'));
    if (i < 0 || !this.picks[i]) return false;
    this.picks[i](); return true;
  }
  hide() { this.el.hidden = true; this.open = false; }
}

function lastText(c: Choices): string {
  const bits: string[] = [c.option === 'metro' ? 'metro' : 'cab'];
  for (const [e, ch] of Object.entries(c.picks)) bits.push(`${e.replace(/-/g, ' ')}: ${ch.replace(/-/g, ' ')}`);
  return bits.join(', ');
}

// ------------------------------------------------------------------ the poster bazaar

/**
 * The stall's pick list (docs/previews/bazaar-mock.png): the 16 posters as cards cut from the atlas, and walking past.
 * Esc closes it undecided; the stall stays until you decide.
 */
export class BazaarView {
  constructor(private el: HTMLElement) {}
  open = false;
  show(B: BazaarData, onPick: (slug: string | null) => void) {
    const W = 112, cards = Object.entries(POSTERS.posters as Record<string, { x: number; y: number; w: number; h: number; text: string }>).map(([slug, c]) => {
      const k = W / c.w; // the atlas is ATLAS_W wide
      return `<button data-slug="${esc(slug)}" title="${esc(c.text)}"><i style="width:${W}px;height:${Math.round(c.h * k)}px;background-position:${-c.x * k}px ${-c.y * k}px;background-size:${Math.round(ATLAS_W * k)}px auto"></i><span>${esc(c.text)}</span></button>`;
    }).join('');
    this.el.innerHTML = `<div class="bz-top">${esc(B.site.place.en)}</div><h2>Posters <span lang="hi">· पोस्टर</span></h2>`
      + `<div class="bz-sub">Pick one to carry: ${B.shop.costMin} min · or walk past, empty-handed</div>`
      + `<div class="bz-grid">${cards}<button data-none class="bz-none"><b>Walk past</b><span>You read as anyone else on the street</span></button></div>`;
    this.el.querySelectorAll<HTMLElement>('.bz-grid i').forEach((i) => { i.style.backgroundImage = `url(${POSTERS.image})`; });
    this.el.querySelectorAll<HTMLButtonElement>('button[data-slug]').forEach((b) => { b.onclick = () => { this.hide(); onPick(b.dataset.slug!); }; });
    this.el.querySelector<HTMLButtonElement>('button[data-none]')!.onclick = () => { this.hide(); onPick(null); };
    this.el.hidden = false; this.open = true;
    this.el.querySelector<HTMLButtonElement>('button')?.focus();
  }
  /** Esc closes it, undecided; every other key stays with the panel (no walking while you choose). */
  key(code: string): boolean { if (!this.open) return false; if (code === 'Escape') this.hide(); return true; }
  hide() { this.el.hidden = true; this.open = false; }
}

// ------------------------------------------------------------------ the journey card

/** On arrival: how the journey went, what it costs the stage, and the rumour's debrief with its sources. */
export class JourneyCardView {
  constructor(private el: HTMLElement) {}
  private t = 0;
  show(line: string, stage: string, debrief: { en: string; source?: string } | null) {
    this.el.innerHTML = `<div class="jc-line">${esc(line)}</div><div class="jc-stage">${esc(stage)}</div>`
      + (debrief ? `<div class="jc-debrief">${esc(debrief.en)}${debrief.source ? `<small>Source: ${esc(debrief.source)}</small>` : ''}</div>` : '')
      + '<div class="jc-hint">Click or press any key</div>';
    this.el.hidden = false; this.t = 12;
    this.el.onclick = () => this.hide();
  }
  get open() { return !this.el.hidden; }
  update(dt: number) { if (!this.el.hidden && (this.t -= dt) <= 0) this.hide(); }
  hide() { this.el.hidden = true; }
}

/** What the arrival means for the stage, in a line. */
export function stageText(a: Arrival, gatherMaxS: number): string {
  if (a.skipGather) return 'Dropped at Jantar Mantar Road. The kafila gathers at Patel Chowk without you: hold the road till they come.';
  if (a.by === 'walk') return 'You walk in from Connaught Place. The kafila has already set off: catch up with it.';
  if (a.stage === 'march') return 'The kafila has already set off. Catch up with the march.';
  if (a.gatherGoneS <= 0) return 'Early. Help the kafila gather at the gate.';
  return `The gathering has been going ${a.gatherGoneS} s of ${gatherMaxS}. Join it.`;
}

// ------------------------------------------------------------------ sound

let audio: AudioContext | null = null;
/** The station chime: two soft tones (drawn in code, no recording). */
export function chime() {
  try {
    audio ??= new AudioContext();
    const t = audio.currentTime;
    for (const [f, at] of [[659, 0], [523, 0.22]] as const) {
      const o = audio.createOscillator(), v = audio.createGain();
      o.frequency.value = f; o.type = 'sine';
      v.gain.setValueAtTime(0, t + at); v.gain.linearRampToValueAtTime(0.08, t + at + 0.02); v.gain.exponentialRampToValueAtTime(0.0001, t + at + 0.5);
      o.connect(v).connect(audio.destination); o.start(t + at); o.stop(t + at + 0.55);
    }
  } catch { /* no audio */ }
}
